// Firm detection utilities based on hostname

export type FirmDomain = "pregodonnell" | "bpmlaw" | "tysonmendes" | "taylorday" | "namanhowell" | "hourglass" | "unknown";

/**
 * Detects the current firm based on window.location.hostname
 */
export function getFirmDomain(): FirmDomain {
  const hostname = window.location.hostname;

  if (hostname.includes("pregodonnell")) return "pregodonnell";
  if (hostname.includes("bpmlaw")) return "bpmlaw";
  if (hostname.includes("tysonmendes")) return "tysonmendes";
  if (hostname.includes("taylorday")) return "taylorday";
  if (hostname.includes("namanhowell")) return "namanhowell";
  if (hostname.includes("hourglass") || hostname.includes("localhost")) return "hourglass";

  return "unknown";
}

/**
 * Returns true if the current firm is Preg O'Donnell
 */
export function isPregODonnell(): boolean {
  return getFirmDomain() === "pregodonnell";
}

/**
 * Returns true if the current firm is Taylor Day
 */
export function isTaylorDay(): boolean {
  return getFirmDomain() === "taylorday";
}

/**
 * Returns true if the current firm is Naman Howell
 */
export function isNamanHowell(): boolean {
  return getFirmDomain() === "namanhowell";
}

/**
 * Determines if activity code is required for a given matter.
 * For Preg O'Donnell: required when matter has activity_group, optional when NULL
 * For Taylor Day: required when matter has activity_group, optional when NULL
 * For Naman Howell: required when matter has task_group, optional when task_group is NULL
 * For other firms: always required
 */
export function isActivityRequired(
  matterId: string | undefined,
  matters: { matter_id: string; activity_group: string; task_group?: string }[]
): boolean {
  // For Naman Howell: activity is required when matter has task_group, optional when task_group is NULL
  if (isNamanHowell()) {
    if (!matterId) return true;
    const selectedMatter = matters.find((m) => m.matter_id === matterId);
    if (!selectedMatter) return true;
    return !!(selectedMatter.task_group && selectedMatter.task_group.trim() !== "");
  }

  // Activity is always required for firms other than Preg O'Donnell and Taylor Day
  if (!isPregODonnell() && !isTaylorDay()) {
    return true;
  }

  // If no matter selected, activity is required (default behavior)
  if (!matterId) {
    return true;
  }

  // Find the selected matter
  const selectedMatter = matters.find((m) => m.matter_id === matterId);
  if (!selectedMatter) {
    return true;
  }

  // For Preg O'Donnell and Taylor Day: required when activity_group exists, optional when NULL
  return !!(selectedMatter.activity_group && selectedMatter.activity_group.trim() !== "");
}

/**
 * Determines if task code is required for a given matter.
 * For Taylor Day: required when matter has task_group, not required when NULL
 * For Naman Howell: required when matter has task_group, not supported when NULL
 * For other firms: always required
 */
export function isTaskRequired(
  matterId: string | undefined,
  matters: { matter_id: string; task_group: string }[]
): boolean {
  // Task is always required for firms other than Taylor Day and Naman Howell
  if (!isTaylorDay() && !isNamanHowell()) {
    return true;
  }

  // If no matter selected, task is required (default behavior)
  if (!matterId) {
    return true;
  }

  // Find the selected matter
  const selectedMatter = matters.find((m) => m.matter_id === matterId);
  if (!selectedMatter) {
    return true;
  }

  // For Taylor Day: not required when task_group is NULL
  // For Naman Howell: not supported when task_group is NULL
  return !!(selectedMatter.task_group && selectedMatter.task_group.trim() !== "");
}
